/*
 * <load-more-grid>: loads the next page of a product grid in place.
 *
 * Markup contract (see snippets/vitto-load-more.liquid and the grid sections):
 *   <load-more-grid data-section-id data-current-page data-has-next-page data-mode="button|infinite">
 *     <ul data-load-more-list>…products…</ul>
 *     <div data-load-more-control><a href="…?page=2" data-load-more-button>Load more</a></div>
 *   </load-more-grid>
 *
 * The next page is requested from the *current* URL, so active filters and sort order survive.
 * If anything goes wrong the link is followed normally.
 */

/** URL of the section-rendered next page, keeping every query parameter (filters, sort) of `href`. */
function nextPageUrl(href, sectionId, page) {
  const url = new URL(href, 'https://placeholder.invalid');
  url.searchParams.set('section_id', sectionId);
  url.searchParams.set('page', String(page));
  return url.pathname + url.search;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { nextPageUrl };
}

if (typeof customElements !== 'undefined' && !customElements.get('load-more-grid')) {
  customElements.define(
    'load-more-grid',
    class LoadMoreGrid extends HTMLElement {
      constructor() {
        super();
        this.isLoading = false;
        this.observer = undefined;
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        // Delegated, so it keeps working after the control is replaced by the next page's.
        this.addEventListener('click', this.onClick);
        this.observeControl();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.observer?.disconnect();
      }

      onClick(event) {
        const button = event.target.closest('[data-load-more-button]');
        if (!button) return;
        event.preventDefault();
        this.loadNextPage();
      }

      observeControl() {
        this.observer?.disconnect();
        if (this.dataset.mode !== 'infinite' || !('IntersectionObserver' in window)) return;

        const control = this.querySelector('[data-load-more-control]');
        if (!control) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) this.loadNextPage();
          },
          { rootMargin: '0px 0px 400px 0px' },
        );
        this.observer.observe(control);
      }

      navigate(href) {
        if (href) window.location.href = href;
      }

      async loadNextPage() {
        if (this.isLoading || this.dataset.hasNextPage !== 'true') return;
        this.isLoading = true;
        this.classList.add('vt-load-more-grid--loading');

        const fallbackHref = this.querySelector('[data-load-more-button]')?.getAttribute('href');
        const nextPage = Number(this.dataset.currentPage) + 1;

        try {
          const response = await fetch(nextPageUrl(window.location.href, this.dataset.sectionId, nextPage));
          if (!response.ok) throw new Error(`Load more failed with status ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fetched = doc.querySelector(`load-more-grid[data-section-id="${this.dataset.sectionId}"]`);
          const fetchedList = fetched?.querySelector('[data-load-more-list]');
          const list = this.querySelector('[data-load-more-list]');
          if (!fetched || !fetchedList || !list) throw new Error('Load more: unexpected response');

          list.append(...fetchedList.children);
          this.dataset.currentPage = fetched.dataset.currentPage || String(nextPage);
          this.dataset.hasNextPage = fetched.dataset.hasNextPage;

          this.querySelector('[data-load-more-control]')?.remove();
          const control = fetched.querySelector('[data-load-more-control]');
          if (control) this.append(control);
        } catch (error) {
          console.error(error);
          this.navigate(fallbackHref);
        } finally {
          this.isLoading = false;
          this.classList.remove('vt-load-more-grid--loading');
          this.observeControl();
        }
      }
    },
  );
}
